/**
 * Provenance and language as the service recorded them.
 *
 * The service decides these when it writes a vehicle, and returns them with the
 * content. They are carried through unmapped because they are the answer to
 * "where did this come from", and any label derived from them is a lossy
 * rendering of that answer rather than a replacement for it.
 */
export interface IContentProvenance {
  /** Description provenance. Older records carry it here only. */
  source?: string
  descriptionSource?: string
  highlightSource?: string
  descriptionLanguage?: string
  highlightLanguage?: string
  /** Retained pre-translation text, which the service uses to restore or re-translate. */
  descriptionSourceSnapshot?: string
  descriptionSourceHash?: string
  descriptionTranslatedFrom?: string
  descriptionSnapshotSource?: string
  highlightSourceSnapshot?: string
  highlightSourceHash?: string
  highlightTranslatedFrom?: string
  highlightSnapshotSource?: string
  descriptionLocked?: boolean
}

export interface IDescription extends IContentProvenance {
  description: string
  value?: string
  highlight?: string
}

export interface IAi2Description extends IContentProvenance {
  description: string
  value?: string
  highlight?: string
}

export interface IDescriptions {
  year: string
  make: string
  model: string
  trim: string
  default: IDescription
  ai2: IAi2Description
  a5: IDescription
  message?: string // for error messages
}

export interface IGetDescriptionRequestParams {
  accountId: string
  vin: string
  [key: string]: any
}

export interface IUpdateDescriptionRequestParams {
  accountId: string
  vin: string
  source: string
  description?: string
  highlight?: string
  descriptionPrompt?: string
  highlightPrompt?: string
}

export interface IGenerateDescriptionRequestParams {
  accountId: string
  content?: string
  promptText?: string
  format?: AiDescriptionChangeRequest
  vin: string
  generationType?: GenerationType
  originalContent?: string
  intent?: 'new' | 'revise'
}

export enum GenerationType {
  Description = 'description',
  Highlight = 'highlight',
}

export interface IJAMSUserAttributes {
  Name: string
  Value: string
}

export interface IJAMSAuthResponse {
  UserAttributes: IJAMSUserAttributes[]
  UserStatus: string
}

export interface IGetDescriptionsRequestParams {
  accountId: string
  [key: string]: any
}

export interface IAccountSettings {
  config: {
    accountName: string
    accountLogo: string
    accountNameFriendly: string
    [key: string]: any
  }
  [key: string]: any
}

export enum Tone {
  Default = 'Default',
  Promotional = 'Promotional',
  Professional = 'Professional',
  Technical = 'Technical',
  Informative = 'Informative',
  Luxury = 'Luxury',
  Benefits = 'Focused on benefits',
}

export enum Length {
  Default = 'Default',
  Shorter = 'Shorter',
  Longer = 'Longer',
  WordCount = 'Word Count',
}

export enum Format {
  Default = 'Default',
  Paragraphs = 'Paragraphs',
  BulletedLists = 'Bulleted Lists',
  Combo = 'Combo',
}

export interface AiDescriptionChangeRequest {
  tones: Tone[]
  lengths: (Length | string)[]
  formats: (Format | string)[]
  minWordCount: string
  maxWordCount: string
  highlightBulletCount?: string
  highlightCharacterLimit?: string
}

/**
 * Highlight provenance, mirroring the service enum exactly: these are the only
 * values a save may carry.
 *
 * `ai2` is deliberately absent. It is a legacy value that appears in stored
 * records and in what the editor emits, but the service does not accept it for
 * highlights, so it is translated to `custom-ai` on the way out. See
 * `contentSource.ts`, which owns that mapping.
 *
 * `ai-translated` is the exception to "a save may carry it": it is provenance
 * older records still hold, and it is read but never sent.
 */
export enum IHighlightSource {
  CustomAi = 'custom-ai',
  AutoAi = 'auto-ai',
  UserProvided = 'user-provided',
  AiTranslated = 'ai-translated',
  Unknown = 'unknown',
  None = 'none',
}

export interface IVehicleTableData {
  vin: string
  stockNumber: string
  year: string
  make: string
  model: string
  trim: string
  descriptionStatus: string
  highlightStatus: string
  descriptionLocked?: boolean
}

export interface IApiProductSettings {
  [key: string]: any
}

// Prompt-related types that match the backend service
export enum PromptType {
  DESCRIPTION = 'description',
  HIGHLIGHT = 'highlight',
}

export interface IBackendPrompt {
  id: string
  title: string
  content: string
  type: PromptType
  createdAt?: string
  updatedAt?: string
  deletedAt?: string
}

export interface IAccountPrompts {
  accountName: string
  prompts: IBackendPrompt[]
}

export interface IPromptCreateRequest {
  title: string
  content: string
  type: PromptType
}

export interface IPromptUpdateRequest {
  title?: string
  content?: string
  type?: PromptType
}

export interface IPromptsApiResponse {
  accountName: string
  prompts: IBackendPrompt[]
}

export interface IPromptApiResponse {
  accountName: string
  prompt: IBackendPrompt
}

// Guardrail-related types
export enum GuardrailType {
  DESCRIPTION = 'description',
  HIGHLIGHT = 'highlight',
}

export interface IBackendGuardrail {
  id: string
  title: string
  content: string
  type: GuardrailType
  isActive: boolean
  priority: number
  createdAt?: string
  updatedAt?: string
  deletedAt?: string
}

export interface IGlobalGuardrails {
  version: string
  lastUpdated: string
  guardrails: IBackendGuardrail[]
}

export interface IGuardrailCreateRequest {
  title: string
  content: string
  type: GuardrailType
  isActive?: boolean
  priority?: number
}

export interface IGuardrailUpdateRequest {
  title?: string
  content?: string
  isActive?: boolean
  priority?: number
}

export interface IGuardrailsApiResponse {
  guardrails: IBackendGuardrail[]
}

export interface IGuardrailApiResponse {
  guardrail: IBackendGuardrail
}
